import {
  CODE_FONT, PLATE_INK, fitFont, plateCodeLabel, plateCodeRect, plateQrBox,
} from './plateGeometry';
import { PlateRecipe } from '../types';

/**
 * A placa composta DIRETO no canvas 2D.
 *
 * O palco do diálogo é DOM (`StationPlate`), e por um tempo os arquivos saíram
 * dele, fotografando o nó com `html-to-image`. Funcionava, mas a foto de DOM
 * cobra caro por placa: clona a árvore copiando ~340 propriedades computadas
 * por elemento, embute as webfonts do documento, serializa tudo num SVG com
 * `foreignObject` — com o gabarito inteiro em base64 dentro — e manda o
 * browser decodificar esse SVG de volta. Num posto de doze bombas isso é doze
 * vezes o mesmo gabarito de 1200×1200 indo e voltando por base64.
 *
 * E a peça não precisa de nada disso: ela é um bitmap de fundo, um QR e uma
 * linha de texto. Desenhada à mão, cada placa custa um `drawImage` e um
 * `fillText`, e o gabarito é decodificado UMA vez para o lote inteiro.
 *
 * O preço de desenhar duas vezes a mesma peça — aqui e no DOM do palco — é a
 * chance de as duas divergirem. É por isso que toda a geometria e toda a
 * tipografia vêm do `plateGeometry`, que os dois consomem: aqui não se
 * arredonda, não se mede e não se escolhe retângulo nenhum.
 */

/** O QR já codificado, lido do `qrcode.react`. */
export interface PlateQrSource {
  /** Quantos módulos o código tem de lado. */
  modules: number;
  /** O contorno dos módulos escuros, em unidades de MÓDULO. */
  path: string;
}

/**
 * Lê o QR de um `<QRCodeSVG>` já montado.
 *
 * O `qrcode.react` não exporta o codificador, só os componentes — e reimplantar
 * QR à mão para um lote seria manter um segundo codificador que pode discordar
 * do que o preview mostra. Montar o SVG fora da tela e ler dele o `viewBox` (o
 * número de módulos) e o `d` do path (os módulos escuros) reaproveita o
 * codificador da lib sem desenhar o SVG: o mesmo código que o palco mostra.
 *
 * O componente emite exatamente dois paths — o fundo e, por último, os módulos.
 */
export const qrSourceOf = (svg: SVGSVGElement | null): PlateQrSource => {
  const modules = Number((svg?.getAttribute('viewBox') || '').split(/\s+/)[2]);
  const paths = svg?.querySelectorAll('path');
  const path = paths?.length ? paths[paths.length - 1].getAttribute('d') : null;
  if (!modules || !path) { throw new Error('O QR da placa não chegou a ser montado'); }
  return { modules, path };
};

/**
 * O QR, do tamanho de um módulo por pixel, ampliado SEM suavização.
 *
 * Preencher o path já na escala final deixaria a aresta de cada módulo
 * anti-serrilhada, porque canvas não tem o `shape-rendering: crispEdges` que o
 * SVG do palco usa. Desenhar um módulo por pixel e ampliar por vizinho mais
 * próximo dá o mesmo resultado que o `crispEdges`: borda dura, com os módulos
 * caindo em pixel inteiro, que é o que a câmera do app lê melhor impresso.
 */
const drawQr = (
  ctx: CanvasRenderingContext2D,
  qr: PlateQrSource,
  box: { x: number; y: number; size: number },
  ink: string,
) => {
  const tile = document.createElement('canvas');
  tile.width = qr.modules;
  tile.height = qr.modules;
  const tileContext = tile.getContext('2d');
  if (!tileContext) { throw new Error('Não foi possível desenhar o QR'); }
  tileContext.fillStyle = ink;
  tileContext.fill(new Path2D(qr.path));

  const smoothing = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tile, box.x, box.y, box.size, box.size);
  ctx.imageSmoothingEnabled = smoothing;
};

/** O código na tarja, centrado como o palco centra. */
const drawCode = (ctx: CanvasRenderingContext2D, recipe: PlateRecipe, code: string, ink: string) => {
  const rect = plateCodeRect(recipe);
  const label = plateCodeLabel(code);
  const { peso } = recipe.slots.codigo.fonteSugerida;
  const { px, spacing } = fitFont(label, rect, recipe.slots.codigo.fonteSugerida);
  const centerX = rect.x + rect.w / 2;
  const centerY = rect.y + rect.h / 2;

  ctx.fillStyle = ink;
  ctx.font = `${peso} ${px}px ${CODE_FONT}`;
  ctx.textAlign = 'left';
  // O palco centra o texto num flex de `line-height: 1`, que é a mesma conta
  // que o `middle` do canvas faz: metade do que a fonte sobe menos metade do
  // que ela desce.
  ctx.textBaseline = 'middle';

  // `letterSpacing` do canvas é recente; onde não existir, o espaçamento sai
  // caractere a caractere, que é a mesma régua a um custo um pouco maior.
  if (!spacing || !('letterSpacing' in ctx)) {
    if (!spacing) {
      ctx.fillText(label, centerX - ctx.measureText(label).width / 2, centerY);
      return;
    }
    const chars = [...label];
    const widths = chars.map(char => ctx.measureText(char).width);
    const total = widths.reduce((sum, width) => sum + width, 0) + spacing * (chars.length - 1);
    let x = centerX - total / 2;
    chars.forEach((char, index) => {
      ctx.fillText(char, x, centerY);
      x += widths[index] + spacing;
    });
    return;
  }

  ctx.letterSpacing = `${spacing}px`;
  // O espaçamento entra depois de cada caractere, o ÚLTIMO inclusive, e a
  // medição o inclui — descontá-lo é o que o `marginRight` negativo faz no
  // palco para devolver o meio de verdade.
  const width = ctx.measureText(label).width - spacing;
  ctx.fillText(label, centerX - width / 2, centerY);
  ctx.letterSpacing = '0px';
};

export interface DrawPlateOptions {
  /** O gabarito, já decodificado. O mesmo serve para o lote inteiro. */
  art: CanvasImageSource;
  recipe: PlateRecipe;
  /** O código de abastecimento cru — o mesmo que o QR codifica. */
  code: string;
  qr: PlateQrSource;
  /** Fração do quadrado do QR que o código ocupa, de 0 a 1. */
  qrScale?: number;
  ink?: string;
}

/**
 * Compõe uma placa no contexto recebido, que tem de ser do tamanho do canvas
 * da receita. Reaproveitar o mesmo contexto entre as placas do lote é de
 * propósito: um canvas de 1200×1200 por bomba seria memória à toa.
 */
export const drawPlate = (
  ctx: CanvasRenderingContext2D,
  { art, recipe, code, qr, qrScale = 1, ink = PLATE_INK }: DrawPlateOptions,
) => {
  const { w, h } = recipe.canvas;

  // O branco por baixo não é decoração: gabarito com transparência deixaria
  // passar a placa anterior, já que o contexto é reaproveitado.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  // Com largura e altura explícitas, gabarito de tamanho diferente do canvas
  // da receita é esticado — o mesmo que o `width: 100%` faz no palco.
  ctx.drawImage(art, 0, 0, w, h);

  drawQr(ctx, qr, plateQrBox(recipe, qrScale), ink);
  drawCode(ctx, recipe, code, ink);
};
